import { BusinessUnitKey, type BusinessUnitRuntime } from '@/modules/bu/registry.base';
import CleepoCheckoutSuccessModal from '@/modules/cleepo/components/cart/CleepoCheckoutSuccessModal.vue';
import { ProjectStatus } from '@/modules/cleepo/interfaces/project.interface';
import { cleeService } from '@/modules/cleepo/services/clee.service';
import { useProjectStore } from '@/modules/cleepo/stores/project.store';
import { i18n } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { isAdminRole } from '@/utils/role.utils';
import { toast } from '@/utils/toast.utils';

// Discovered by `src/modules/bu/registry.runtime.ts`.
export const key = BusinessUnitKey.Cleepo;

const runtime: BusinessUnitRuntime = {
  init: () => useProjectStore().init(),
  clear: () => useProjectStore().clear(),
  resolveEntryRoute() {
    const authStore = useAuthStore();
    const uiStore = useUiStore();
    const projectStore = useProjectStore();
    const { t } = i18n.global as any;

    return {
      validateAccount: async () => {
        const hasAccounts = authStore.user?.accounts && authStore.user?.accounts?.length > 0;
        if (!authStore.user?.current_account_id) {
          if (isAdminRole(authStore.user)) {
            return 'AdminSelectAccountView';
          }
          return hasAccounts ? 'CleepoSelectAccountView' : 'CleepoWizardCreateAccountView';
        }

        if (!authStore.currentAccount?.active) {
          return hasAccounts ? 'CleepoSelectAccountView' : 'CleepoWizardCreateAccountView';
        }
      },
      handleNoCurrentOrder: async () => {
        if (isAdminRole(authStore.user)) {
          return 'AdminSelectAccountView';
        }

        const hasAccounts = authStore.user?.accounts && authStore.user?.accounts?.length > 0;
        if (hasAccounts) {
          return 'CleepoSelectAccountView';
        }

        const hasProjects = projectStore.projects.length > 0;
        if (!hasProjects) {
          //TODO: if user has no projects, create the project.
          return 'CleepoWizardCreateAccountView';
        }

        const activeProject = projectStore.projects.find((p) => p.status === ProjectStatus.ACTIVE);

        try {
          if (activeProject) {
            await projectStore.setSelectedProject(activeProject.id);
          } else {
            await projectStore.setSelectedProject(projectStore.projects[0].id);
          }
        } catch (error) {
          toast.error({ message: t('notifications.account_not_configured_error') });
          uiStore.openSupportDrawer();
          return 'CleepoAuthLoginView';
        }
      },
    };
  },
  currentEntityId: () => useProjectStore().currentProject?.id,
  onOrderCancel(order_id, type) {
    if (type !== 'NOW') return undefined;

    const projectId = useProjectStore().projects.find(
      (project) => Number(project.order_id) === order_id,
    )?.id;
    if (!projectId) throw new Error((i18n.global as any).t('notifications.cancel_order_error'));

    return () => cleeService.cancelProject(projectId);
  },
  checkoutSuccessComponent: CleepoCheckoutSuccessModal,
};

export default runtime;
